// src/components/ui/Input.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  error?: string;
  hint?: string;
  containerClassName?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  icon,
  required,
  optional,
  error,
  hint,
  type = 'text',
  className = '',
  containerClassName = '',
  onFocus,
  onBlur,
  disabled,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <div className={`space-y-2 ${containerClassName}`}>
      {label && (
        <label className="text-gray-700 dark:text-gray-300 text-sm font-medium flex items-center gap-2">
          {icon && <span className="text-gray-500 dark:text-gray-400">{icon}</span>}
          {label}
          {required && <span className="text-red-500 text-sm">*</span>}
          {optional && <span className="text-gray-400 text-xs ml-1">(Optional)</span>}
        </label>
      )}
      
      <motion.div
        animate={{ scale: isFocused ? 1.02 : 1 }}
        transition={{ duration: 0.2 }}
        className="relative"
      >
        <input
          type={inputType}
          className={`
            w-full px-4 py-3 rounded-xl border-2
            bg-white dark:bg-[#2C2C2E]
            text-gray-900 dark:text-gray-100
            transition-all duration-200
            focus:outline-none
            ${error 
              ? 'border-red-500 dark:border-red-500 focus:border-red-500' 
              : 'border-gray-200 dark:border-gray-700 focus:border-purple-500 dark:focus:border-purple-400'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800' : ''}
            ${className}
          `}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          {...props}
        />
        
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </motion.div>
      
      {error && (
        <p className="text-red-500 dark:text-red-400 text-xs mt-1">{error}</p>
      )}
      
      {hint && !error && (
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{hint}</p>
      )}
    </div>
  );
};